import Icon, { FolderFilled, RightOutlined } from '@ant-design/icons';
import { Button, Descriptions, Space, Table, Tooltip } from 'antd';
import Search from 'antd/lib/input/Search';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import React, { useEffect, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { Link, useParams } from 'react-router-dom';
import taskPermissionLinkApi from '../../../../../api/TaskPermissionLinkApi';
import taskSyncApi from '../../../../../api/TaskSyncApi';
import LayoutComponent from '../../../../../components/LayoutComponent/LayoutComponent';
import TaskStatusComponent from '../../../../../components/TaskStatusComponent/TaskStatusComponent';
import { Page } from '../../../../../model/elements';
import { TaskPermissionActionFolder, TaskPermissionLink, TaskSync } from '../../../../../model/entities';
import { TaskStatusType } from '../../../../../model/types';
import { ReactComponent as BackSvg } from '../../../../../resources/images/back.svg';
import { ReactComponent as RefreshSvg } from '../../../../../resources/images/refresh.svg';
import notificationService from '../../../../../services/NotificationService';
import tableService from '../../../../../services/TableService';
import styles from './TaskPermissionLinkPage.module.scss';
import taskPermissionActionFolderApi from '../../../../../api/TaskPermissionActionFolderApi';

/**
 * Returns the task permission link page.
 * @returns the task permission link page.
 */
const TaskPermissionLinkPage = (): React.ReactElement => {
    /*** HOOKS ***/

    const intl = useIntl();
    const params = useParams<ParamsType>();
    const [task, setTask] = useState<TaskSync>();
    const [taskLink, setTaskLink] = useState<TaskPermissionLink>();
    const [taskPermissionActionFoldersPage, setTaskPermissionActionFoldersPage] =
        useState<Page<TaskPermissionActionFolder>>();
    const [folders, setFolders] = useState<TaskPermissionActionFolder[]>([]);
    const [loading, setLoading] = useState<boolean>();
    const [searchText, setSearchText] = useState<string>();
    const [parentId, setParentId] = useState<number>();

    /*** EFFECTS ***/

    useEffect(() => {
        const init = async () => {
            try {
                setLoading(true);
                const responses = await Promise.all([
                    taskPermissionLinkApi.get(+params.id),
                    listTaskActionFolders(0, tableService.pageSize, 'name', true, +params.id, undefined, undefined),
                ]);

                const taskLink = responses[0];
                const task = await taskSyncApi.get(taskLink.taskId!);
                setTask(task);
                setTaskLink(taskLink);
            } catch (error) {
                notificationService.displayError(error, intl);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, [intl, params.id]);

    /*** METHODS ***/

    const list = async () => {
        try {
            setLoading(true);
            const page = 0;
            const size = taskPermissionActionFoldersPage?.size || tableService.pageSize;
            const sortField = taskPermissionActionFoldersPage?.sort.field || 'name';
            const sortOrder = taskPermissionActionFoldersPage?.sort.order || true;
            const responses = await Promise.all([
                taskPermissionLinkApi.get(+params.id),
                listTaskActionFolders(page, size, sortField, sortOrder, +params.id, searchText, parentId),
            ]);

            const taskLink = responses[0];
            const task = await taskSyncApi.get(taskLink.taskId!);
            setTask(task);
            setTaskLink(taskLink);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const paginate = async (pagination: TablePaginationConfig, filters: any, sorter: any) => {
        try {
            setLoading(true);
            const page = pagination.current! - 1;
            const pageSize = pagination.pageSize!;
            const sortField = sorter.field;
            const sortOrder = sorter.order === 'ascend';
            listTaskActionFolders(page, pageSize, sortField, sortOrder, taskLink!.id!, searchText, parentId);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const search = async (searchText: string) => {
        try {
            setLoading(true);
            setSearchText(searchText);
            const page = 0;
            const pageSize = taskPermissionActionFoldersPage!.size;
            const sortField = taskPermissionActionFoldersPage!.sort.field!;
            const sortOrder = taskPermissionActionFoldersPage!.sort.order!;
            listTaskActionFolders(page, pageSize, sortField, sortOrder, taskLink!.id!, searchText, parentId);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const filterByFolder = async (taskPermissionActionFolder: TaskPermissionActionFolder) => {
        try {
            setLoading(true);
            setParentId(taskPermissionActionFolder.id);
            const page = 0;
            const pageSize = taskPermissionActionFoldersPage?.size || tableService.pageSize;
            const sortField = taskPermissionActionFoldersPage?.sort.field || 'folder';
            const sortOrder = taskPermissionActionFoldersPage?.sort.order || true;
            listTaskActionFolders(
                page,
                pageSize,
                sortField,
                sortOrder,
                taskLink!.id!,
                searchText,
                taskPermissionActionFolder.id,
            );
            const updatedFolders = Object.assign([], folders);
            updatedFolders.push(taskPermissionActionFolder);
            setFolders(updatedFolders);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    const listTaskActionFolders = async (
        page: number,
        size: number,
        sortField: string,
        sortOrder: boolean,
        taskSyncLinkId: number,
        searchText?: string,
        parentId?: number,
    ) => {
        const taskPermissionActionFoldersPage = await taskPermissionActionFolderApi.list(
            page,
            size,
            sortField,
            sortOrder,
            taskSyncLinkId,
            searchText,
            parentId,
        );

        setTaskPermissionActionFoldersPage(taskPermissionActionFoldersPage);
    };

    /**
     * Function for updating folder route in the table
     */
    const updateFolderList = (taskPermissionActionFolder: TaskPermissionActionFolder, buttonClicked: boolean) => {
        try {
            setLoading(true);
            const updatedFolderList: TaskPermissionActionFolder[] = folders;

            // We find we have the element in the array from we are going to remove elements
            const indexOfObject = folders.findIndex((object) => {
                return object.id === taskPermissionActionFolder.id;
            });

            // Remove from the list the elements from as the select one
            if (buttonClicked) {
                updatedFolderList.splice(indexOfObject, folders.length + 1 - (indexOfObject + 1));
            } else {
                updatedFolderList.splice(indexOfObject + 1, folders.length + 1 - (indexOfObject + 1));
            }

            // Filter the table again by the last element of the list
            const parentId =
                updatedFolderList.length > 0 ? updatedFolderList[updatedFolderList.length - 1].id : undefined;

            const page = 0;
            const pageSize = taskPermissionActionFoldersPage?.size || tableService.pageSize;
            const sortField = taskPermissionActionFoldersPage?.sort.field || 'folder';
            const sortOrder = taskPermissionActionFoldersPage?.sort.order || true;
            listTaskActionFolders(page, pageSize, sortField, sortOrder, taskLink!.id!, searchText, parentId);

            setFolders(updatedFolderList);
        } catch (error) {
            notificationService.displayError(error, intl);
        } finally {
            setLoading(false);
        }
    };

    /*** COMPONENTS ***/
    const renderLink = (
        taskPermissionActionFolder: TaskPermissionActionFolder,
        element: React.ReactElement | string,
        isTaskPermissionActionFolderLink?: boolean,
    ): React.ReactElement | undefined => {
        if (isTaskPermissionActionFolderLink) {
            return <Link to={`/task-permission-action-folders/${taskPermissionActionFolder.id}`}>{element}</Link>;
        } else if (taskPermissionActionFolder.subfoldersCount > 0) {
            return (
                <span onClick={() => filterByFolder(taskPermissionActionFolder)} className={styles.link}>
                    {element}
                </span>
            );
        } else {
            return <span className={styles.text}>{element}</span>;
        }
    };

    const placeholder: string = intl.formatMessage({ id: 'taskPermissionLink.search' });
    const items = taskPermissionActionFoldersPage ? taskPermissionActionFoldersPage.content : [];
    const columns: ColumnsType<TaskPermissionActionFolder> = [
        {
            title: <FormattedMessage id="taskPermissionLink.folder" />,
            dataIndex: 'name',
            key: 'name',
            sorter: true,
            defaultSortOrder: 'ascend',
            render: (value: string, taskPermissionActionFolder: TaskPermissionActionFolder) => {
                if (taskPermissionActionFolder.subfoldersCount > 0) {
                    return renderLink(
                        taskPermissionActionFolder,
                        <Button icon={<FolderFilled />} type="link">
                            {taskPermissionActionFolder.name}
                        </Button>,
                    );
                } else {
                    return renderLink(
                        taskPermissionActionFolder,
                        <Button icon={<FolderFilled />} type="text" className={styles.text}>
                            {taskPermissionActionFolder.name}
                        </Button>,
                    );
                }
            },
        },
        {
            title: <FormattedMessage id="taskPermissionLink.audit.created" />,
            dataIndex: 'auditCreated',
            key: 'created',
            sorter: true,
            width: 240,
            align: 'center',
            render: (value: string, taskPermissionActionFolder: TaskPermissionActionFolder) =>
                renderLink(
                    taskPermissionActionFolder,
                    <FormattedDate
                        value={taskPermissionActionFolder.audit.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />,
                ),
        },
        {
            title: <FormattedMessage id="taskPermissionLink.audit.updated" />,
            dataIndex: 'auditUpdated',
            key: 'updated',
            sorter: true,
            width: 240,
            align: 'center',
            render: (value: string, taskPermissionActionFolder: TaskPermissionActionFolder) =>
                renderLink(
                    taskPermissionActionFolder,
                    <FormattedDate
                        value={taskPermissionActionFolder.audit.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />,
                ),
        },
        {
            title: <FormattedMessage id="taskPermissionLink.status" />,
            dataIndex: 'status',
            key: 'status',
            sorter: true,
            width: 200,
            align: 'center',
            render: (status: TaskStatusType, taskPermissionActionFolder: TaskPermissionActionFolder) =>
                renderLink(taskPermissionActionFolder, <TaskStatusComponent status={status} />, true),
        },
        {
            title: <FormattedMessage id="taskPermissionLink.subfoldersStatus" />,
            dataIndex: 'subfoldersStatus',
            key: 'subfoldersStatus',
            sorter: true,
            width: 200,
            align: 'center',
            render: (subfoldersStatus: TaskStatusType, taskPermissionActionFolder: TaskPermissionActionFolder) =>
                taskPermissionActionFolder.subfoldersCount > 0 &&
                renderLink(taskPermissionActionFolder, <TaskStatusComponent status={subfoldersStatus} />),
        },
    ];

    return (
        <LayoutComponent pageId="tasks">
            <Descriptions
                title={
                    <>
                        <span className={styles.title}>
                            #{task?.id} - {task?.name} - <FormattedMessage id="taskLink.title" />
                        </span>
                        <TaskStatusComponent status={taskLink?.status} />
                        <Space>
                            <Tooltip title={<FormattedMessage id="button.back" />}>
                                <Link to={`/task-permissions/${task?.id}`}>
                                    <Button type="ghost" size="small" icon={<Icon component={BackSvg} />} />
                                </Link>
                            </Tooltip>
                            <Tooltip title={<FormattedMessage id="button.refresh" />}>
                                <Button
                                    type="ghost"
                                    size="small"
                                    icon={<Icon component={RefreshSvg} />}
                                    onClick={list}
                                />
                            </Tooltip>
                        </Space>
                    </>
                }
                column={2}
                size="small"
                className={styles.summary}
            >
                <Descriptions.Item label={<FormattedMessage id="task.source" />}>
                    {task?.source?.provider} &gt; {task?.source?.name} &gt; {taskLink?.source?.project?.name} &gt;{' '}
                    {taskLink?.source?.folder?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.target" />}>
                    {task?.target?.provider} &gt; {task?.target?.name}
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.created" />}>
                    <FormattedDate
                        value={taskLink?.audit?.created as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
                <Descriptions.Item label={<FormattedMessage id="task.audit.updated" />}>
                    <FormattedDate
                        value={taskLink?.audit?.updated as any}
                        day="2-digit"
                        month="2-digit"
                        year="numeric"
                        hour="2-digit"
                        minute="2-digit"
                        second="2-digit"
                    />
                </Descriptions.Item>
            </Descriptions>
            <div className="toolbar">
                <Search placeholder={placeholder} onSearch={search} size="large" className="search" />
            </div>
            {folders.map((folder, index) => {
                if (index === 0) {
                    return (
                        <>
                            <Button
                                icon={<RightOutlined className={styles.rightArrow} />}
                                type="text"
                                onClick={() => updateFolderList(folder, true)}
                                className={styles.rightArrowButton}
                            ></Button>{' '}
                            <Button type="link" onClick={() => updateFolderList(folder, false)}>
                                {folder.name}
                            </Button>
                        </>
                    );
                } else {
                    return (
                        <>
                            <RightOutlined className={styles.rightArrowIcon} />{' '}
                            <Button type="link" onClick={() => updateFolderList(folder, false)}>
                                {folder.name}
                            </Button>
                        </>
                    );
                }
            })}
            <Table
                dataSource={items}
                columns={columns}
                pagination={tableService.createPagination(taskPermissionActionFoldersPage, {
                    position: ['bottomRight'],
                })}
                rowKey="id"
                onChange={paginate}
                sortDirections={['ascend', 'descend']}
                showSorterTooltip={false}
                loading={loading}
            />
        </LayoutComponent>
    );
};

export default TaskPermissionLinkPage;

type ParamsType = { id: string };
